'use strict';

import firebase from 'firebase/app';
import 'firebase/auth';
import axios from 'axios';
import isAdmin from '../services/http/isAdmin';
import JWT from '../services/jwt/JWT';
import isSuperAdmin from '../services/jwt/isSuperAdmin';
import getHeader from '../config/header';

const API_URL = process.env.VUE_APP_API_URL || "http://localhost:3001/api";
import getUID from '../services/firebase/getUID';

const User = {

    authenticate(username, password) {
        return new Promise((resolve, reject) => {
            if (!username || !password) {
                reject({ message: "Email and password are mandatory" });
            }

            firebase.auth().signInWithEmailAndPassword(username, password)
            .then((response) => {
                response.user.getIdToken()
                .then(async (token) => {
                    let permissions = await isAdmin(token);
                    if (permissions.isAdmin === true || permissions.isSuperAdmin === true) {
                        localStorage.setItem('access_token', token);
                        
                        if (permissions.isAdmin === true) {
                            let uuid = await getUID();
                            let info = await this.getInfo();
                            localStorage.setItem('superAdmin', JWT.sign(permissions.isSuperAdmin, uuid, info.subscription.subscription_id));

                        } else {
                            // Encode the permissions for this user and then store it locally
                            localStorage.setItem('superAdmin', JWT.sign(permissions.isSuperAdmin));
                        }
                        
                        resolve(true);
                    } else {
                        reject({ message: permissions.error.response.data.error });
                    }
                })
            })
            .catch(error => {
                reject(error);
            });
        })
            
    },

    logout() {
        return new Promise((resolve, reject) => {
            firebase.auth().signOut()
            .then(() => {
                localStorage.clear();
                resolve(true);
            }).catch(error => {
                reject(error);
            });
        }); 
    },

    isSuperAdmin(token) {
        return isSuperAdmin(token) === true;
    },

    resetPassword(email) {
        return new Promise((resolve, reject) => {
            if (!email) {
                reject({ message: "Please provide a valid email" });
            }
            let auth = firebase.auth();

            auth.sendPasswordResetEmail(email)
            .then(() => {
                resolve(true);
            }).catch((error) => {
                resolve(false);
            });

        });
    },

    changePassword(newPassword) {
        return new Promise(async (resolve, reject) => {
            if (!newPassword) {
                resolve({error: "Password missing"});
            }
    
            let user = firebase.auth().currentUser;
            
            user.updatePassword(newPassword).then(function() {
                resolve(true);
            }).catch(function(error) {
                resolve({ error: error.message });
            });
        })
     },
    
    async getInfo() {
        try {
            let token = localStorage.getItem('access_token');
            let user = JWT.decode(token);
            let userID = user.user_id;

            let header = await getHeader();

            let response = await axios.get(API_URL + '/user/' + userID, header);
    
            return response.data;
    
        } catch(error) {
            return false;
        }
    },


    async isActive() {
        try {
            let user = JWT.decode(localStorage.getItem('superAdmin'));
            let header = await getHeader();

            let response = await axios.get(API_URL + '/user/' + user.UUID + '/subscriptionStatus', header);

            return response.data.active;
    
        } catch(error) {
            return false;
        }
    },

    async getBySignUpToken(token) {
        try {
            let response = await axios.get(API_URL + '/user/token/' + token);

            return response.data;
    
        } catch(error) {
            return false;
        }
    },

    async getByID(firebaseID) {
        try {
            let header = await getHeader();

            let response = await axios.get(API_URL + '/user/' + firebaseID, header);
    
            return response.data;
    
        } catch(error) {
            return false;
        }
    },

    async update(userID, name, displayName, jobTitle, phoneNumber, email) {
        try {
            if (!userID || !name || !displayName || !jobTitle || !phoneNumber) {
                return { error: "A mandatory field is missing" };
            }

            const header = await getHeader();

            let response = await axios.patch(API_URL + '/user/' + userID, {
				"name": name,
                "displayName": displayName,
                "jobTitle": jobTitle,
                "phoneNumber": phoneNumber,
                "email": email
            }, header);

            if (response.data.id) {
                return response.data;
            }

            return false;
    
        } catch(error) {
            return false;
        }
    },

    async updateCuratorMuseum(userID, museumIDs) {
        try {
            if (!userID || museumIDs.length < 1) {
                return { error: "A mandatory field is missing" };
            }

            const header = await getHeader();
            
            let response = await axios.post(API_URL + '/user/' + userID + '/updateMuseums', {
                "museumIds": museumIDs
            }, header);

            if (response.data.message.includes('Claims updated')) {
                return true;
            }

            return false;
    
        } catch(error) {
            return false;
        }
    },

    async uploadImage(filename, content) {
        try {
            if (!filename || !content) {
                return { error: 'A mandatory field is missing' };
            }

            const header = await getHeader();

            let response = await axios.post(API_URL + '/user/uploadProfilePic', {
                    "fileName": filename,
                    "content": content
                }, header);
            
            if (response.data.id) {
                return response.data;
            } 

            return false;

        } catch(error) {
            return false;
        }
    },

    async delete(userID) {
        try {
            if (!userID) {
                return { error: "User ID is missing" };
            }

            const header = await getHeader();

            let response = await axios.delete(API_URL + '/user/' + userID, header);
            
            return response.data.message.includes("Successfully deleted the user");

        } catch(error) {
            return false;
        }
    },

    async getFirebaseProfile(uid) {
        return new Promise(async (resolve, reject) => {
            try {
                let user = await firebase.auth().getUser(uid);
                resolve (user.email);
            } catch(error) {
                reject(error);
            }
        })
    },

    async requestPlatinumAccount(name, phone, email) {
        try {
            if (!name || !phone || !email) {
                return { error: "A mandatory field is missing"};
            }

            const header = await getHeader();

            let response = await axios.post(API_URL + '/user/platinumRequest', {
                "name": name,
                "email": email,
                "phoneNumber": phone
            }, header);

            return response.data.message.includes("Email sent successfully");

        } catch(error) {
            return false;
        }
    },

    async getCurrentPlan() {
        try {
            let token = localStorage.getItem('access_token');
            let user = JWT.decode(token);
            let userID = user.user_id;

            let header = await getHeader();
            let response = await axios.get(API_URL + '/user/' + userID, header);

            return response.data.subscription.subscription_status;
    
        } catch(error) {
            return false;
        }
    }

}

export default User;